import { getRouteProductKeyFromRegistry } from "@/lib/constants/app-routes";
import { isProd } from "@/lib/env";

// ---------------------------------------------------------------------------
// enforcement toggle
// ---------------------------------------------------------------------------

// TODO: flip to `true` once the mobile app handles entitlement-gated 403s.
// uses an in-code flag instead of posthog because this runs on every v1 api
// request — a network round-trip per call would add latency and create a
// posthog availability dependency on the hot path. switch to an env var or
// posthog (with caching) when ready for gradual per-org rollout.
// while this returns false, all requireProduct / requireFeature /
// enforceEntitlements calls are no-ops — the route-level configs stay in
// place as documentation of intent.
export function isEntitlementsEnforcementEnabled(): boolean {
  return false;
}

// ---------------------------------------------------------------------------
// feature keys — the atomic units of access control
// ---------------------------------------------------------------------------

export const FEATURES = {
  CORE_CRM: "core-crm",
  PAYMENTS: "payments",
  REVIEWS: "reviews",
  WEBSITE: "website",
  CUSTOM_DOMAIN: "custom-domain",
  GEOTAGGING: "geotagging",
  SOCIAL_MEDIA: "social-media",
  GBP_OPTIMIZER: "gbp-optimizer",
  VOICE_ASSISTANT: "voice-assistant",
} as const;

export type FeatureKey = (typeof FEATURES)[keyof typeof FEATURES];

// ---------------------------------------------------------------------------
// product keys — each maps to a stripe product or manual grant
// ---------------------------------------------------------------------------

export const PRODUCT_KEYS = {
  CRM: "crm",
  MARKETING_KIT: "marketing-kit",
  VOICE_ASSISTANT: "voice-assistant",
  MK_CRM_BUNDLE: "mk-crm-bundle",
} as const;

export type ProductKey = (typeof PRODUCT_KEYS)[keyof typeof PRODUCT_KEYS];

// ---------------------------------------------------------------------------
// subscription source
// ---------------------------------------------------------------------------

export const SUBSCRIPTION_SOURCES = {
  STRIPE: "STRIPE",
  MANUAL: "MANUAL",
} as const;

export type SubscriptionSource = (typeof SUBSCRIPTION_SOURCES)[keyof typeof SUBSCRIPTION_SOURCES];

// ---------------------------------------------------------------------------
// feature definition — declares access constraints per feature
// ---------------------------------------------------------------------------

export interface FeatureDefinition {
  key: FeatureKey;
  name: string;
  /** feature is locked during trial periods */
  paidOnly?: boolean;
}

// ---------------------------------------------------------------------------
// product definition — maps a product to its stripe ids and feature set
// ---------------------------------------------------------------------------

export interface PriceIds {
  production: string;
  development: string;
}

export interface ProductDefinition {
  key: ProductKey;
  stripeProductIds: PriceIds;
  name: string;
  /** null = no trial available */
  trialDays: number | null;
  features: FeatureDefinition[];
  /**
   * bundle expansion — when this product is active, the resolver also marks
   * each listed product key as active. this lets requireProduct("marketing-kit")
   * pass for a subscriber who purchased the mk+crm bundle.
   *
   * only set on bundle products. individual products leave this undefined.
   */
  expandsTo?: readonly ProductKey[];
}

const DEFAULT_TRIAL_DAYS = isProd() ? 14 : 1;

export const PRODUCTS: readonly ProductDefinition[] = [
  {
    key: PRODUCT_KEYS.CRM,
    stripeProductIds: {
      production: "prod_U7QM2wyzTbWpcB",
      development: "prod_U5dtcV5rw9t8sL",
    },
    name: "CRM",
    trialDays: DEFAULT_TRIAL_DAYS,
    features: [
      { key: FEATURES.CORE_CRM, name: "Core CRM" },
      { key: FEATURES.PAYMENTS, name: "Payments", paidOnly: true },
      { key: FEATURES.REVIEWS, name: "Reviews Management" },
    ],
  },
  {
    key: PRODUCT_KEYS.MARKETING_KIT,
    // production id intentionally reuses the legacy "premium" stripe product
    // (prod_QFZMmK3Vmblv8j) — sales/gtm wanted to keep existing subscribers on
    // the same product rather than migrating them to a new one. this means the
    // same id appears in LEGACY_STRIPE_PRODUCTS.PREMIUM below; both map to
    // PRODUCT_KEYS.MARKETING_KIT so the duplicate entry is harmless.
    stripeProductIds: {
      production: "prod_QFZMmK3Vmblv8j",
      development: "prod_U5duI8NP2Cwbkk",
    },
    name: "Marketing Kit",
    trialDays: DEFAULT_TRIAL_DAYS,
    features: [
      { key: FEATURES.WEBSITE, name: "Website" },
      { key: FEATURES.CUSTOM_DOMAIN, name: "Custom Domain", paidOnly: true },
      { key: FEATURES.GEOTAGGING, name: "Geotagging" },
      { key: FEATURES.REVIEWS, name: "Reviews Management" },
      { key: FEATURES.SOCIAL_MEDIA, name: "Social Media Management" },
      { key: FEATURES.GBP_OPTIMIZER, name: "GBP Optimizer" },
    ],
  },
  {
    key: PRODUCT_KEYS.VOICE_ASSISTANT,
    stripeProductIds: {
      production: "prod_U85NkvGwot4Uih",
      development: "prod_U5dtyveDNzDPAT",
    },
    name: "AI Voice Assistant",
    trialDays: null,
    features: [{ key: FEATURES.VOICE_ASSISTANT, name: "AI Voice Assistant" }],
  },
  // ---- bundles ----
  {
    key: PRODUCT_KEYS.MK_CRM_BUNDLE,
    stripeProductIds: {
      production: "prod_U8q9KVMfUZEre2",
      development: "prod_U8ojZqOot9T9Fw",
    },
    name: "Marketing Kit + CRM Bundle",
    trialDays: DEFAULT_TRIAL_DAYS,
    features: [
      { key: FEATURES.CORE_CRM, name: "Core CRM" },
      { key: FEATURES.PAYMENTS, name: "Payments", paidOnly: true },
      { key: FEATURES.WEBSITE, name: "Website" },
      { key: FEATURES.CUSTOM_DOMAIN, name: "Custom Domain", paidOnly: true },
      { key: FEATURES.GEOTAGGING, name: "Geotagging" },
      { key: FEATURES.REVIEWS, name: "Reviews Management" },
      { key: FEATURES.SOCIAL_MEDIA, name: "Social Media Management" },
      { key: FEATURES.GBP_OPTIMIZER, name: "GBP Optimizer" },
    ],
    expandsTo: [PRODUCT_KEYS.CRM, PRODUCT_KEYS.MARKETING_KIT],
  },
] as const;

// ---------------------------------------------------------------------------
// overlap maps — derived from expandsTo, used for subscription conflict checks
// ---------------------------------------------------------------------------

/** maps a bundle product to the individual products it supersedes */
export const PRODUCT_SUPERSEDES: ReadonlyMap<ProductKey, readonly ProductKey[]> = new Map(
  PRODUCTS.filter((p) => p.expandsTo).map((p) => [p.key, p.expandsTo!])
);

/** reverse map: individual product → bundle product keys that cover it */
export const PRODUCT_COVERED_BY: ReadonlyMap<ProductKey, readonly ProductKey[]> = (() => {
  const map = new Map<ProductKey, ProductKey[]>();
  for (const product of PRODUCTS) {
    if (!product.expandsTo) continue;
    for (const childKey of product.expandsTo) {
      const existing = map.get(childKey) ?? [];
      existing.push(product.key);
      map.set(childKey, existing);
    }
  }
  return map;
})();

/**
 * returns all product keys that become redundant when `activatedKey` is active.
 * for a bundle, this is its component products. for an individual product, empty.
 */
export function getSupersededProducts(activatedKey: ProductKey): readonly ProductKey[] {
  return PRODUCT_SUPERSEDES.get(activatedKey) ?? [];
}

/**
 * returns all bundle product keys that already cover `productKey`.
 * used by checkout/grant guards to prevent subscribing to an individual
 * product when a covering bundle is already active.
 */
export function getCoveringBundles(productKey: ProductKey): readonly ProductKey[] {
  return PRODUCT_COVERED_BY.get(productKey) ?? [];
}

/**
 * returns all product keys that grant access to `targetKey` — the key itself
 * plus any bundles whose expandsTo includes it. used by cron/query filters
 * that need to find orgs with access to a particular product.
 *
 * e.g. getProductKeysGranting("marketing-kit") → ["marketing-kit", "mk-crm-bundle"]
 */
export function getProductKeysGranting(targetKey: ProductKey): ProductKey[] {
  return [targetKey, ...(PRODUCT_COVERED_BY.get(targetKey) ?? [])];
}

// ---------------------------------------------------------------------------
// legacy product ids — old basic/premium plans, all map to marketing-kit
// ---------------------------------------------------------------------------

export const LEGACY_STRIPE_PRODUCTS = {
  BASIC: { production: "prod_QFZLc7q4mf7Pxa", development: "prod_QGKSFUZZwTa2Gs" },
  PREMIUM: { production: "prod_QFZMmK3Vmblv8j", development: "prod_QGKTix07Qb4Jno" },
} as const;

// non-stripe sentinel values stored in Organization.stripeProductIds for partners
export const EXTERNAL_PRODUCT_IDS = {
  EXTERNAL_BASIC: "EXTERNAL_BASIC",
  EXTERNAL_PREMIUM: "EXTERNAL_PREMIUM",
} as const;

export const ALL_EXTERNAL_IDS: ReadonlySet<string> = new Set(Object.values(EXTERNAL_PRODUCT_IDS));

// stripe products we recognize but intentionally do NOT map to a product key:
// legacy/grandfathered plans that no longer grant entitlements. the billing
// webhook reads this to avoid paging sentry with "unmapped stripe product" on
// every renewal of these plans. a genuinely unknown product still warns.
const HOSTING_PRODUCT_ID = "prod_RYD6KyKvMXiCrA";
export const IGNORED_STRIPE_PRODUCT_IDS: ReadonlySet<string> = new Set<string>([HOSTING_PRODUCT_ID]);

// ---------------------------------------------------------------------------
// lookup maps — built once at module load
// ---------------------------------------------------------------------------

const ENV_KEY = isProd() ? "production" : "development";

/** stripe product id → product key (used by webhooks to derive productKey) */
export const STRIPE_PRODUCT_TO_KEY: ReadonlyMap<string, ProductKey> = new Map([
  ...PRODUCTS.map((p) => [p.stripeProductIds[ENV_KEY], p.key] as const),
  // legacy basic/premium products all map to marketing-kit for webhook compat
  [LEGACY_STRIPE_PRODUCTS.BASIC[ENV_KEY], PRODUCT_KEYS.MARKETING_KIT],
  [LEGACY_STRIPE_PRODUCTS.PREMIUM[ENV_KEY], PRODUCT_KEYS.MARKETING_KIT],
]);

/** product key → full product definition */
export const PRODUCT_BY_KEY: ReadonlyMap<ProductKey, ProductDefinition> = new Map(PRODUCTS.map((p) => [p.key, p]));

/** all valid product keys as a set for runtime validation */
export const VALID_PRODUCT_KEYS: ReadonlySet<ProductKey> = new Set(PRODUCTS.map((p) => p.key));

/** type guard — narrows an arbitrary string to ProductKey if it's recognized */
export function isValidProductKey(key: string): key is ProductKey {
  const keys: ReadonlySet<string> = VALID_PRODUCT_KEYS;
  return keys.has(key);
}

// ---------------------------------------------------------------------------
// stripe resolution helpers
// ---------------------------------------------------------------------------

/** resolves the stripe product id for a product key in the current env */
export function getStripeProductId(productKey: ProductKey): string | null {
  const product = PRODUCT_BY_KEY.get(productKey);
  if (!product) return null;
  return product.stripeProductIds[ENV_KEY];
}

// ---------------------------------------------------------------------------
// route → product map — delegates to the route registry
// ---------------------------------------------------------------------------

/** resolves the product key that gates a given pathname, or null if ungated */
export function getRouteProductKey(pathname: string): ProductKey | null {
  return getRouteProductKeyFromRegistry(pathname);
}

// ---------------------------------------------------------------------------
// product nav upsell config — copy + pricing shown in sidebar upsell popovers
// ---------------------------------------------------------------------------

interface ConditionalPrice {
  /** show this price label when the org already subscribes to the given product */
  whenHasProduct: ProductKey;
  priceLabel: string;
}

export interface ProductUpsellConfig {
  key: ProductKey;
  name: string;
  description: string;
  priceLabel: string;
  conditionalPricing?: ConditionalPrice;
  learnMoreHref: string;
}

export const PRODUCT_UPSELL_CONFIG: Record<ProductKey, ProductUpsellConfig> = {
  [PRODUCT_KEYS.CRM]: {
    key: PRODUCT_KEYS.CRM,
    name: "Rebolt CRM",
    description: "Scheduling, quotes, invoices, and payments\u2014all in one operating system built for service pros.",
    priceLabel: "$99",
    conditionalPricing: { whenHasProduct: PRODUCT_KEYS.MARKETING_KIT, priceLabel: "$49" },
    learnMoreHref: "/dashboard/billing",
  },
  [PRODUCT_KEYS.MARKETING_KIT]: {
    key: PRODUCT_KEYS.MARKETING_KIT,
    name: "Marketing Kit",
    description: "Your complete digital presence. SEO, social media, and review management all in one place.",
    priceLabel: "$299",
    learnMoreHref: "/dashboard/billing",
  },
  [PRODUCT_KEYS.VOICE_ASSISTANT]: {
    key: PRODUCT_KEYS.VOICE_ASSISTANT,
    name: "AI Voice Assistant",
    description:
      "On a ladder, driving between jobs, or after hours? Rebolt\u2019s AI Voice Assistant answers every call instantly.",
    priceLabel: "$99",
    learnMoreHref: "/dashboard/billing",
  },
  [PRODUCT_KEYS.MK_CRM_BUNDLE]: {
    key: PRODUCT_KEYS.MK_CRM_BUNDLE,
    name: "Marketing Kit + CRM",
    description: "The full Rebolt platform\u2014website, SEO, social, CRM, scheduling, quotes, invoices, and payments.",
    priceLabel: "$349",
    learnMoreHref: "/dashboard/billing",
  },
};

/**
 * Returns the upsell config with the correct `priceLabel` resolved against
 * the org's active subscriptions. When `conditionalPricing` matches an
 * active product the discounted label is substituted in.
 */
export function resolveUpsellConfig(
  config: ProductUpsellConfig,
  hasProduct: (product: ProductKey) => boolean
): ProductUpsellConfig {
  if (config.conditionalPricing && hasProduct(config.conditionalPricing.whenHasProduct)) {
    return { ...config, priceLabel: config.conditionalPricing.priceLabel };
  }
  return config;
}
